import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'

import {
  useProductStore,
  useProductStoreActions,
} from '@/modules/product/stores/product.store'
import { useMainStore } from '@/modules/shared/stores/main.store'

import { ORDER_FORM_CART } from './cart.constants'
import { useOrderFormComponentStoreActions } from '../../stores/order-form-component.store'
import {
  useOrderFormStore,
  useOrderFormStoreActions,
} from '../../stores/order-form.store'
import { useProducts } from '../../hooks/use-products.hook'
import { useOrderFormCalculation } from '../../hooks/use-order-form-calculation.hook'
import useOrderFormParams from '../../hooks/use-order-form-params.hook'
import { useAutoCalculateOrderFormJson } from '@/modules/product/hooks/use-auto-calculate-order-form-json.hook'

type TEventClick = {
  selected: number
}

export const useCart = () => {
  const [itemsOffset, setItemsOffset] = useState(0)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  // Evita busca inicial aqui para não duplicar com o Catálogo
  const { products } = useProducts({ skipInitialFetch: true })
  const { calculateOrderForm } = useOrderFormCalculation()
  const { autoCalculateEnabled } = useOrderFormParams()

  const handleChangeCheckBox = (id: string) => {
    const index = selectedItems.findIndex((item) => item === id)

    if (index === -1) {
      setSelectedItems([...selectedItems, id])
    } else {
      const newSelectedItems = selectedItems.filter((item) => item !== id)
      setSelectedItems(newSelectedItems)
    }
  }

  const { setConfirmation, setCloseConfirmation } = useMainStore().actions

  const {
    jsonOrderForm,
    paramsLayoutOrderForm,
    isCalculationOrder,
    operationType,
  } = useOrderFormStore()

  const form = useForm({
    values: {
      discount1: jsonOrderForm?.desconto1,
      discount2: jsonOrderForm?.desconto2,
      discount3: jsonOrderForm?.desconto3,
      discount4: jsonOrderForm?.desconto4,
    },
  })

  const { setIsOpenModalLoadExcel } = useOrderFormComponentStoreActions
  const { setIsCalculationOrder, setJsonOrderForm, setUpdatedProductDetails } =
    useOrderFormStoreActions()
  const { productsInsertedInOrderToCalculation } = useProductStore()
  const { setProductsInsertedInOrderToCalculation } = useProductStoreActions

  const paramsButtonExcel = paramsLayoutOrderForm?.carrinho?.find(
    (item) => item.campo === 'showImportExcel',
  )

  const paramsFormDiscount = paramsLayoutOrderForm?.carrinho?.find(
    (item) => item.campo === 'discounts',
  )

  useAutoCalculateOrderFormJson();

  const itemsPerPage = 25

  const endOffset = itemsOffset + itemsPerPage

  const currentItems = jsonOrderForm?.itemsCalculo?.slice(
    itemsOffset,
    endOffset,
  )
  const pageCount = Math.ceil(
    (jsonOrderForm?.itemsCalculo?.length ?? 0) / itemsPerPage,
  )

  const handleChangeValues = (fieldInJson: string, valueToInsert: string) => {
    const value = valueToInsert.replace(',', '.')
    const newJsonOrderForm = {
      ...jsonOrderForm,
      [fieldInJson]: Number(value),
    }

    setJsonOrderForm(newJsonOrderForm)

    if (autoCalculateEnabled) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }

      debounceTimeout.current = setTimeout(() => {
        if (!isCalculationOrder) setIsCalculationOrder(true)
        calculateOrderForm(newJsonOrderForm)
      }, 750)
    } else {
      setIsCalculationOrder(true)
    }
  }
  const handleChangePageOfPagination = (event: TEventClick) => {
    const newOffset =
      (event.selected * itemsPerPage) %
      (jsonOrderForm?.itemsCalculo?.length ?? 0)
    setItemsOffset(newOffset)
  }

  const handleClearCart = () => {
    setConfirmation({
      title: 'Atenção!',
      isOpen: true,
      message: 'Deseja realmente limpar o carrinho?',
      onClose: () => {
        setCloseConfirmation()
      },
      onConfirm: () => {
        const newJsonOrderForm = {
          ...jsonOrderForm,
          itemsCalculo: [],
        }

        setProductsInsertedInOrderToCalculation([
          ...productsInsertedInOrderToCalculation,
          ...jsonOrderForm.itemsCalculo.map((item) => item.fkProduto),
        ])

        setIsCalculationOrder(true)

        setJsonOrderForm(newJsonOrderForm)
        setCloseConfirmation()
        setUpdatedProductDetails(true)
      },
    })
  }
  const handleRemoveManyItems = () => {
    setConfirmation({
      title: 'Atenção!',
      isOpen: true,
      message: 'Essa ação não poderá ser desfeita!',
      onClose: () => {
        setCloseConfirmation()
      },
      onConfirm: () => {
        setProductsInsertedInOrderToCalculation([
          ...productsInsertedInOrderToCalculation,
          ...selectedItems,
        ])
        const newItems = jsonOrderForm?.itemsCalculo?.filter(
          (item) => !selectedItems.includes(item.fkProduto),
        )

        const newJsonOrderForm = {
          ...jsonOrderForm,
          itemsCalculo: newItems,
        }

        setJsonOrderForm(newJsonOrderForm)
        setSelectedItems([])
        setIsCalculationOrder(true)
        setCloseConfirmation()
        setUpdatedProductDetails(true)
      },
    })
  }

  const handleRemoveItem = (index: number) => {
    setConfirmation({
      title: 'Atenção!',
      isOpen: true,
      message: 'Essa ação não poderá ser desfeita!',
      onClose: () => {
        setCloseConfirmation()
      },
      onConfirm: () => {
        const productInArray = productsInsertedInOrderToCalculation?.find(
          (item) => item === currentItems[index]?.fkProduto,
        )

        if (!productInArray) {
          setProductsInsertedInOrderToCalculation([
            ...productsInsertedInOrderToCalculation,
            currentItems[index]?.fkProduto,
          ])
        }

        const newItems = jsonOrderForm?.itemsCalculo?.filter(
          (_, i) => i !== index,
        )

        const newJsonOrderForm = {
          ...jsonOrderForm,
          itemsCalculo: newItems,
        }

        setJsonOrderForm(newJsonOrderForm)
        setIsCalculationOrder(true)
        setCloseConfirmation()
        setUpdatedProductDetails(true)
      },
    })
  }

  const handleModalLoadExcel = () => {
    setIsOpenModalLoadExcel(true)
  }

  const defaultFieldsOfDiscount = ORDER_FORM_CART.map((item) => ({
    ...item,
  }))

  if (paramsLayoutOrderForm?.carrinho) {
    for (const itemApi of paramsLayoutOrderForm.carrinho) {
      const index = defaultFieldsOfDiscount.findIndex(
        (item) => item.campo === itemApi.campo,
      )

      if (index !== -1) {
        defaultFieldsOfDiscount[index].label = itemApi.label
          ? itemApi.label
          : defaultFieldsOfDiscount[index].label

        defaultFieldsOfDiscount[index].enabled =
          itemApi.enabled ?? defaultFieldsOfDiscount[index].enabled
        defaultFieldsOfDiscount[index].visible =
          itemApi.visible ?? defaultFieldsOfDiscount[index].visible
      }
    }
  }

  // Removido o efeito que disparava busca inicial no carrinho para evitar duplicidade

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  return {
    pageCount,
    currentItems,
    isViewOrder: operationType === 'view',
    renderDefaultFieldsOfDiscount: defaultFieldsOfDiscount,
    isShowFormDiscount: paramsFormDiscount?.visible !== false,
    isShowButtonLoadExcel: paramsButtonExcel?.visible === true,
    form,
    selectedItems,
    handleChangeValues,
    handleModalLoadExcel,
    handleChangePageOfPagination,
    handleRemoveItem,
    handleChangeCheckBox,
    handleRemoveManyItems,
    handleClearCart,
  }
}
